import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '开体育在线',
  url: 'https://www.kaitiyu.com/',
  description: '开体育在线是您首选的体育赛事在线服务平台，涵盖足球、篮球、网球等多项赛事直播与数据分析。',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '朝阳区体育中心大厦18层',
    addressLocality: '北京',
    addressCountry: 'CN',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+86-400-888-6666',
    contactType: 'customer service',
    availableLanguage: 'Chinese',
  },
  sameAs: [
    'https://weibo.com/kaitiyu',
    'https://weixin.qq.com/kaitiyu',
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '开体育在线提供哪些体育项目的赛事直播？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '开体育在线覆盖足球、篮球、网球、电子竞技、棒球、赛车等多项体育赛事，提供实时比分、直播数据和深度分析。',
      },
    },
    {
      '@type': 'Question',
      name: '如何注册成为开体育在线的会员？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过网站首页点击注册按钮，填写基本信息即可完成注册。注册成功后即可享受全部赛事数据服务。',
      },
    },
    {
      '@type': 'Question',
      name: '开体育在线的数据来源是否可靠？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们的数据来源于官方体育赛事机构及认证数据提供商，所有数据均经过多重校验，确保准确性和时效性。',
      },
    },
    {
      '@type': 'Question',
      name: '平台支持移动端访问吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '开体育在线采用响应式设计，完美适配手机、平板和电脑等多种设备，让您随时随地享受体育赛事服务。',
      },
    },
  ],
};

const FORM_URL = 'https://readdy.ai/api/form/d9rf9eue3oq4jqumu0j0';

const milestones = [
  { year: '2018', title: '平台创立', desc: '开体育在线正式成立，以足球赛事数据为核心产品上线。' },
  { year: '2019', title: '品类扩展', desc: '新增篮球、网球赛事频道，用户突破50万。' },
  { year: '2020', title: '技术升级', desc: '引入AI数据分析系统，实时数据处理能力大幅提升。' },
  { year: '2022', title: '电竞上线', desc: '电子竞技频道上线，覆盖LOL、DOTA2等热门游戏赛事。' },
  { year: '2024', title: '千万用户', desc: '平台注册用户突破380万，日均活跃用户超50万。' },
  { year: '2026', title: '行业领先', desc: '成为华语地区领先的体育赛事在线服务平台。' },
];

const values = [
  { icon: 'ri-shield-check-line', title: '专业可靠', desc: '数据来源官方认证，信息准确及时，为您的决策提供坚实依据。' },
  { icon: 'ri-flashlight-line', title: '极速响应', desc: '毫秒级数据更新，不错过赛场上任何一个精彩瞬间。' },
  { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户需求为核心，持续优化产品体验，提供贴心服务。' },
  { icon: 'ri-global-line', title: '全球视野', desc: '覆盖全球主流体育赛事，连接世界各地的体育爱好者。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotEl) formData.delete('phone_alt');

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试。');
          setFormStatus('error');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试。');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查您的网络连接后重试。');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <SEOHead
        title="关于我们"
        description="了解开体育在线——专业的体育赛事在线服务平台。品牌介绍、发展历程、信誉保障和联系方式。"
        canonicalUrl="https://www.kaitiyu.com/about"
      />
      <Navbar />
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: '关于我们' },
        ]}
      />

      <main>
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">关于开体育在线</h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                开体育在线成立于2018年，致力于为全球华语用户提供专业、及时、全面的体育赛事在线服务。
                我们相信体育的力量——它连接世界，点燃激情，创造无限可能。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
              {values.map((v) => (
                <div key={v.title} className="bg-background-100 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-lg">
                    <i className={`${v.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300 hidden lg:block"></div>
              <div className="space-y-8">
                {milestones.map((m, index) => (
                  <div key={m.year} className={`flex flex-col lg:flex-row items-center gap-4 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'} text-center lg:text-inherit`}>
                      <div className="bg-background-50 rounded-lg p-5 border border-background-200/70 inline-block text-left max-w-md">
                        <span className="text-xs font-bold text-primary-500 bg-primary-100 px-2 py-0.5 rounded-full">{m.year}</span>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mt-2 mb-1">{m.title}</h3>
                        <p className="text-sm text-foreground-600">{m.desc}</p>
                      </div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-accent-500 border-4 border-background-100 flex items-center justify-center flex-shrink-0 hidden lg:flex">
                      <div className="w-2 h-2 rounded-full bg-background-50"></div>
                    </div>
                    <div className="flex-1 hidden lg:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                信誉保障
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                    <i className="ri-award-line text-2xl text-accent-500"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">资质认证</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    持有国家互联网信息服务许可证，所有业务均在法律法规框架内合规运营。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                    <i className="ri-lock-line text-2xl text-accent-500"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">数据安全</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    采用国际标准的SSL加密技术，全方位保障用户数据安全与隐私。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                    <i className="ri-star-line text-2xl text-accent-500"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">行业口碑</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    连续三年获得"最佳体育数据平台"称号，用户满意度高达98.5%。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-10">
                有任何问题或建议？请随时与我们联系，我们将在24小时内回复。
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-2 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                      <p className="text-sm text-foreground-600">北京市朝阳区体育中心大厦18层</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h4>
                      <p className="text-sm text-foreground-600">400-888-6666</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">contact@kaitiyu.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                      <p className="text-sm text-foreground-600">周一至周日 9:00 - 22:00</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <form
                    data-readdy-form
                    id="about-contact-form"
                    onSubmit={handleSubmit}
                    className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70 space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="cf-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                        <input
                          type="text"
                          id="cf-name"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-900"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="cf-email" className="block text-sm font-medium text-foreground-700 mb-1">电子邮箱</label>
                        <input
                          type="email"
                          id="cf-email"
                          name="email"
                          required
                          className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-900"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="cf-subject" className="block text-sm font-medium text-foreground-700 mb-1">主题</label>
                      <input
                        type="text"
                        id="cf-subject"
                        name="subject"
                        required
                        className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-900"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="cf-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                      <textarea
                        id="cf-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-900 resize-none"
                        placeholder="请输入您的留言（不超过500字）"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      className="sr-email-confirm"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-md font-semibold text-sm whitespace-nowrap hover:bg-primary-600 transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'loading' ? '提交中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <div className="p-3 bg-secondary-100 text-secondary-800 rounded-md text-sm text-center">
                        <i className="ri-check-line mr-1"></i>感谢您的留言！我们将在24小时内与您联系。
                      </div>
                    )}
                    {formStatus === 'error' && formError && (
                      <div className="p-3 bg-primary-100 text-primary-800 rounded-md text-sm text-center">
                        <i className="ri-error-warning-fill mr-1"></i>{formError}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}